// ══════════════════════════════════════════════════════════════════════════════
//  src/valoraciones.js
//  CUANDO SE ENSEÑAN ESTRELLAS Y CUANDO NO
//
//  ── QUE ESTABA MAL ──────────────────────────────────────────────────────────
//  Todos los profesores de NSL enseñaban «⭐ 5.0». No porque fueran excelentes:
//  el numero estaba escrito a mano en el servidor y en el navegador, y ninguno
//  de los dos habia leido jamas la tabla de valoraciones. La tabla existe, con
//  27 filas — pero **ninguna es de un profesor**: valoran la plataforma y el
//  curso. Asi que el 5,0 no era un dato viejo: era un dato que no existia.
//
//  Costaba dinero, ademas: el bono de nomina pagaba **+$500 por «Rating Elite
//  >=4,8»** a todo el mundo, calculado sobre ese 5,0 por defecto.
//
//  ── EL UMBRAL, Y POR QUE ────────────────────────────────────────────────────
//  Decision de Rob:
//
//    «Define un umbral duro en el codigo (por ejemplo, MIN_REVIEWS = 5). Si el
//     profesor tiene menos de 5 calificaciones, la interfaz de React no
//     renderiza las estrellas numericas; en su lugar, muestra una pastilla con
//     tu diseño Neumorfico que diga "Nuevo" o "Evaluando".»
//
//  El motivo es estadistico antes que estetico. Con una sola valoracion, un 5,0
//  y un 1,0 son igual de creibles —es decir, ninguno lo es—, y sin embargo la
//  pantalla los pinta con la misma autoridad que una media de cincuenta.
//  «4,0 ⭐» calculado sobre dos opiniones no informa: da una precision falsa, y
//  encima castiga al profesor nuevo que tuvo un mal dia.
//
//  Por debajo del umbral la respuesta honesta no es un numero peor: es decir
//  que **todavia no se sabe**.
//
//  ⚠️  El gemelo de `MIN_VALORACIONES` esta en `backend-api/src/valoraciones.py`.
//      Si cambias el numero aqui, cambialo alli. Repartido por las dos partes,
//      el dia que se suba uno el otro sigue pintando estrellas que el servidor
//      considera que no existen.
// ══════════════════════════════════════════════════════════════════════════════

// backend-api/src/valoraciones.py · MIN_VALORACIONES
export const MIN_VALORACIONES = 5;

/**
 * Como se enseña la valoracion de un profesor.
 *
 * Toma lo que manda el servidor y devuelve exactamente que pintar. Ninguna
 * pantalla decide esto por su cuenta: repartido por seis ficheros, el dia que
 * cambie el umbral cinco se quedarian viejos, y volveriamos a tener pantallas
 * que dicen cosas distintas del mismo profesor.
 *
 * @param {object} profesor  con `rating`, `total_valoraciones` y
 *                           `valoracion_estado` tal como los manda la API.
 * @returns {{modo:'estrellas'|'pastilla', texto:string, titulo:string, tono:string}}
 */
export function comoSeEnseña(profesor) {
    const total = Number(profesor?.total_valoraciones ?? 0);
    const nota = profesor?.rating;

    // Con suficientes opiniones, el numero significa algo y se enseña.
    if (total >= MIN_VALORACIONES && typeof nota === 'number') {
        return {
            modo: 'estrellas',
            texto: nota.toFixed(1),
            titulo: `${nota.toFixed(1)} de 5, sobre ${total} valoraciones`,
            tono: 'valorado',
        };
    }

    // Ya hay opiniones, pero no bastan para una media honesta.
    if (total > 0) {
        const faltan = MIN_VALORACIONES - total;
        return {
            modo: 'pastilla',
            texto: 'Evaluando',
            titulo: `${total} de ${MIN_VALORACIONES} valoraciones. `
                + `Faltan ${faltan} para publicar su nota.`,
            tono: 'evaluando',
        };
    }

    // Nadie le ha valorado todavia.
    return {
        modo: 'pastilla',
        texto: 'Nuevo',
        titulo: 'Todavia nadie le ha valorado. '
            + `Su nota se publica a partir de ${MIN_VALORACIONES} valoraciones.`,
        tono: 'nuevo',
    };
}

/**
 * La misma decision, en una linea de texto plano.
 *
 * Existe para los sitios donde no cabe un componente de React: la exportacion a
 * SVG de los horarios, el PDF y el CSV. Antes esos tres escribian
 * `t.rating || '5.0'` cada uno por su cuenta — tres copias de la misma mentira.
 */
export function valoracionEnTexto(profesor) {
    const v = comoSeEnseña(profesor);
    return v.modo === 'estrellas' ? `⭐ ${v.texto}` : v.texto;
}

/**
 * ¿Se le puede pagar un bono por valoracion?
 *
 * Se pregunta explicitamente porque es dinero. Antes el calculo hacia
 * `Number(profesor?.rating ?? 5.0)` y luego `if (rating >= 4.8) bonus += 500`:
 * como ningun profesor tenia nota, **todos cobraban el bono de excelencia**.
 *
 * Sin valoraciones suficientes no hay bono de valoracion. No es un castigo:
 * es que no hay nada que premiar todavia, y los demas bonos —cupo, volumen,
 * disponibilidad— se siguen calculando igual.
 */
export function puedeCobrarBonoPorValoracion(profesor) {
    return Number(profesor?.total_valoraciones ?? 0) >= MIN_VALORACIONES
        && typeof profesor?.rating === 'number';
}
